import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// ==================== INITIAL STATE ====================

const initialState = {
  supplierOrders: [],
  supplierOrderLines: [],
  currentSupplierOrder: null,
  currentSupplierOrderLine: null,
  
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },

  filters: {
    statusId: null,
    supplierId: null,
    shopId: null,
    reference: null,
    recipNumber: null,
    startDate: null,
    endDate: null,
  },
  

  loading: false,
  error: null,
  success: false,
  actionStatus: 'idle',

  selectedSupplierOrders: [],
  selectedSupplierOrderId: null,
};

import {
  getSupplierOrders,
  getActiveSupplierOrders,
  getInactiveSupplierOrders,
  getSupplierOrderById,
  getSupplierOrdersBySupplier,
  getSupplierOrdersByShop,
  getSupplierOrdersByStatus,
  getSupplierOrdersByDateRange,
  getSupplierOrdersPaginated,
  createSupplierOrder,
  updateSupplierOrder,
  softDeleteSupplierOrder,
  hardDeleteSupplierOrder,
  cancelSupplierOrder,
  getSupplierOrderLinesByOrderId,
  getSupplierOrderLineById,
  createSupplierOrderLine,
  updateSupplierOrderLine,
  deleteSupplierOrderLine,
  validateSupplierOrder,  
  confirmSupplierOrder,
} from '../services/supplier.order.service';

// ==================== ASYNC THUNKS  ====================

export const fetchSupplierOrders = createAsyncThunk(
  'supplierOrder/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchActiveSupplierOrders = createAsyncThunk(
  'supplierOrder/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getActiveSupplierOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchInactiveSupplierOrders = createAsyncThunk(
  'supplierOrder/fetchInactive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getInactiveSupplierOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchSupplierOrderById = createAsyncThunk(
  'supplierOrder/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrderById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchSupplierOrdersBySupplier = createAsyncThunk(
  'supplierOrder/fetchBySupplier',
  async (supplierId, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrdersBySupplier(supplierId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchSupplierOrdersByShop = createAsyncThunk(
  'supplierOrder/fetchByShop',
  async (shopId, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrdersByShop(shopId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchSupplierOrdersByStatus = createAsyncThunk(
  'supplierOrder/fetchByStatus',
  async (statusId, { rejectWithValue }) => {
      try {
      if (!statusId) {
        return []; 
      }
      const response = await getSupplierOrdersByStatus(statusId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchSupplierOrdersByDateRange = createAsyncThunk(
  'supplierOrder/fetchByDateRange',
  async (dates, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrdersByDateRange(dates);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchSupplierOrdersPaginated = createAsyncThunk(
  'supplierOrder/fetchPaginated',
  async (filters, { getState, rejectWithValue }) => {
    try {
      const { supplierOrder } = getState();
      const paginationFilters = {
        startDate: filters?.startDate || supplierOrder.filters.startDate,
        endDate: filters?.endDate || supplierOrder.filters.endDate,
        page: filters?.page || supplierOrder.pagination.currentPage,
        pageSize: filters?.pageSize || supplierOrder.pagination.pageSize,
      };
      const response = await getSupplierOrdersPaginated(paginationFilters);
      return response; 
    }catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const createSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/create',
  async (supplierOrderData, { rejectWithValue }) => {
    try {
      const response = await createSupplierOrder(supplierOrderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const updateSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateSupplierOrder(id, data);
      return { id, data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const softDeleteSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/softDelete',
  async (id, { rejectWithValue }) => {
    try {
      await softDeleteSupplierOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const hardDeleteSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/hardDelete',
  async (id, { rejectWithValue }) => {
    try {
      await hardDeleteSupplierOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const cancelSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/cancel',
  async (id, { rejectWithValue }) => {
    try {
      await cancelSupplierOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==================== ASYNC THUNKS - SUPPLIER ORDER LINES ====================

export const fetchSupplierOrderLinesByOrderId = createAsyncThunk(
  'supplierOrder/fetchLinesByOrderId',
  async (supplierOrderId, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrderLinesByOrderId(supplierOrderId);
      return { supplierOrderId, lines: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchSupplierOrderLineById = createAsyncThunk(
  'supplierOrder/fetchLineById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getSupplierOrderLineById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const createSupplierOrderLineThunk = createAsyncThunk(
  'supplierOrder/createLine',
  async (lineData, { rejectWithValue }) => {
    try {
      const response = await createSupplierOrderLine(lineData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateSupplierOrderLineThunk = createAsyncThunk(
  'supplierOrder/updateLine',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateSupplierOrderLine(id, data);
      return { id, data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const deleteSupplierOrderLineThunk = createAsyncThunk(
  'supplierOrder/deleteLine',
  async (id, { rejectWithValue }) => {
    try {
      await deleteSupplierOrderLine(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const validateSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/validate',
  async (id, { rejectWithValue }) => {
    try {
      const response = await validateSupplierOrder(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const confirmSupplierOrderThunk = createAsyncThunk(
  'supplierOrder/confirm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await confirmSupplierOrder(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


// ==================== SLICE ====================

const supplierOrderSlice = createSlice({
  name: 'supplierOrder',
  initialState,
  reducers: {
    setSupplierOrderFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSupplierOrderFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    
    setSelectedSupplierOrders: (state, action) => {
      state.selectedSupplierOrders = action.payload;
    },
    setSelectedSupplierOrderId: (state, action) => {
      state.selectedSupplierOrderId = action.payload;
    },
    clearSelectedSupplierOrders: (state) => {
      state.selectedSupplierOrders = [];
      state.selectedSupplierOrderId = null;
    },
    
    clearCurrentSupplierOrder: (state) => {
      state.currentSupplierOrder = null;
    },
    clearCurrentSupplierOrderLine: (state) => {
      state.currentSupplierOrderLine = null;
    },
    clearSupplierOrders: (state) => {
      state.supplierOrders = [];
      state.supplierOrderLines = [];
    },
    
    clearSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetActionStatus: (state) => {
    state.actionStatus = 'idle';
  },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplierOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchSupplierOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchActiveSupplierOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveSupplierOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchActiveSupplierOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchInactiveSupplierOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveSupplierOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchInactiveSupplierOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSupplierOrder = action.payload;
      })
      .addCase(fetchSupplierOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrdersBySupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrdersBySupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchSupplierOrdersBySupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrdersByShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrdersByShop.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchSupplierOrdersByShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrdersByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrdersByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchSupplierOrdersByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrdersByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrdersByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload;
      })
      .addCase(fetchSupplierOrdersByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchSupplierOrdersPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrdersPaginated.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrders = action.payload.items || [];
        state.pagination.totalCount = action.payload.totalCount || 0;
        state.pagination.totalPages = Math.ceil(
          (action.payload.totalCount || 0) / state.pagination.pageSize
        );
      })
      .addCase(fetchSupplierOrdersPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // ========== CREATE ==========
      .addCase(createSupplierOrderThunk.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
         .addCase(createSupplierOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.supplierOrders.unshift(action.payload);
        state.currentSupplierOrder = action.payload;
      })
      .addCase(createSupplierOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      
      // ========== UPDATE ==========
      .addCase(updateSupplierOrderThunk.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateSupplierOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.supplierOrders.findIndex(
          (order) => order.supplierOrderId === action.payload.id
        );
        if (index !== -1) {
          state.supplierOrders[index] = { ...state.supplierOrders[index], ...action.payload.data };
        }
        if (state.currentSupplierOrder?.supplierOrderId === action.payload.id) {
          state.currentSupplierOrder = { ...state.currentSupplierOrder, ...action.payload.data };
        }
      })
      .addCase(updateSupplierOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      
      // ========== SOFT DELETE ==========
      .addCase(softDeleteSupplierOrderThunk.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(softDeleteSupplierOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.supplierOrders = state.supplierOrders.filter(
          (order) => order.supplierOrderId !== action.payload
        );
        if (state.currentSupplierOrder?.supplierOrderId === action.payload) {
          state.currentSupplierOrder = null;
        }
      })
      .addCase(softDeleteSupplierOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      
      // ========== HARD DELETE ==========
      .addCase(hardDeleteSupplierOrderThunk.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(hardDeleteSupplierOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.supplierOrders = state.supplierOrders.filter(
          (order) => order.supplierOrderId !== action.payload
        );
        if (state.currentSupplierOrder?.supplierOrderId === action.payload) {
          state.currentSupplierOrder = null;
        }
      })
      .addCase(hardDeleteSupplierOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      
      // ========== CANCEL ==========
      .addCase(cancelSupplierOrderThunk.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(cancelSupplierOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.supplierOrders.findIndex(
          (order) => order.supplierOrderId === action.payload
        );
        if (index !== -1) {
          state.supplierOrders[index] = { ...state.supplierOrders[index], isCancelled: true, statusName: 'Canceled',};
        }
      })
      .addCase(cancelSupplierOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })

      // ========== VALIDATE (Draft → Waiting) ==========
      .addCase(validateSupplierOrderThunk.pending, (state) => {
        state.actionStatus = 'loading';
      })
      .addCase(validateSupplierOrderThunk.fulfilled, (state, action) => {
        state.actionStatus = 'succeeded';
        const idx = state.supplierOrders.findIndex(
          (o) => o.supplierOrderId === action.payload.supplierOrderId
        );
        if (idx !== -1) state.supplierOrders[idx] = action.payload;
      })
      .addCase(validateSupplierOrderThunk.rejected, (state) => {
        state.actionStatus = 'failed';
      })

      // ========== CONFIRM (Waiting → Confirmed) ==========
      .addCase(confirmSupplierOrderThunk.pending, (state) => {
        state.actionStatus = 'loading';
      })
      .addCase(confirmSupplierOrderThunk.fulfilled, (state, action) => {
        state.actionStatus = 'succeeded';
        const idx = state.supplierOrders.findIndex(
          (o) => o.supplierOrderId === action.payload.supplierOrderId
        );
        if (idx !== -1) state.supplierOrders[idx] = action.payload;
      })
      .addCase(confirmSupplierOrderThunk.rejected, (state) => {
        state.actionStatus = 'failed';
      })
      
      // ========== FETCH LINES BY ORDER ID ==========
      .addCase(fetchSupplierOrderLinesByOrderId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierOrderLinesByOrderId.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierOrderLines = action.payload.lines;
      })
      .addCase(fetchSupplierOrderLinesByOrderId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // ========== CREATE LINE ==========
      .addCase(createSupplierOrderLineThunk.fulfilled, (state, action) => {
        state.supplierOrderLines.push(action.payload);
      })
      
      // ========== UPDATE LINE ==========
      .addCase(updateSupplierOrderLineThunk.fulfilled, (state, action) => {
        const index = state.supplierOrderLines.findIndex(
          (line) => line.supplierOrderLineId === action.payload.id
        );
        if (index !== -1) {
          state.supplierOrderLines[index] = { ...state.supplierOrderLines[index], ...action.payload.data };
        }
      })
      
      // ========== DELETE LINE ==========
      .addCase(deleteSupplierOrderLineThunk.fulfilled, (state, action) => {
        state.supplierOrderLines = state.supplierOrderLines.filter(
          (line) => line.supplierOrderLineId !== action.payload
        );
      });
  },
});

// ==================== EXPORTS ====================

export const {
  setSupplierOrderFilters,
  clearSupplierOrderFilters,
  setCurrentPage,
  setPageSize,
  setSelectedSupplierOrders,
  setSelectedSupplierOrderId,
  clearSelectedSupplierOrders,
  clearCurrentSupplierOrder,
  clearCurrentSupplierOrderLine,
  clearSupplierOrders,
  clearSuccess,
  resetActionStatus,
  clearError,
} = supplierOrderSlice.actions;

export default supplierOrderSlice.reducer;